import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useMutation, useQuery } from "@apollo/client";
import { Modal, H4, Flex } from "orcs-design-system";
import { isEmpty } from "lodash";
import EditMode from "./EditMode";
import PreviewMode from "./PreviewMode";
import { uploadAvatarMutation } from "./mutations/uploadAvatarMutation.graphql";
import { getPersonAvatar } from "./queries/getPersonAvatar.graphql";
import { resizeAndCompressImage, blobToBase64 } from "./utils/canvasUtils";
import { deleteAvatarMutation } from "./mutations/deleteAvatarMutation.graphql";

/** Raster types browsers can decode to canvas; output is still WebP for upload. */
export const DEFAULT_ACCEPTED_IMAGE_TYPES = [
  "image/jpeg",
  "image/jpg",
  "image/pjpeg",
  "image/png",
  "image/x-png",
  "image/gif",
  "image/webp",
];

// TODO: Load image from server and save 2 version. Cropped/Original
const AvatarModal = ({
  isOpen,
  onClose,
  acceptedImageTypes = DEFAULT_ACCEPTED_IMAGE_TYPES,
  shape = "circle",
  personId,
  onSaveAvatar,
}) => {
  const [isNewImage, setIsNewImage] = useState(false);
  const [croppedImage, setCroppedImage] = useState(null);
  const [originalImage, setOriginalImage] = useState(null);
  const [croppedArea, setCroppedArea] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const {
    data,
    loading: isLoadingPersonAvatar,
    refetch: refetchPersonAvatar,
  } = useQuery(getPersonAvatar, {
    variables: { aggregateId: personId },
    skip: !personId,
  });

  const [uploadAvatar, { loading: isUploading }] =
    useMutation(uploadAvatarMutation);

  const setAvatarImages = useCallback(() => {
    if (!isEmpty(data)) {
      const { personAvatar } = data;

      const croppedImageDataUrl = personAvatar.userAvatarCropped;
      const originalImageDataUrl = personAvatar.userAvatarOriginal;

      setCroppedImage(croppedImageDataUrl);
      setOriginalImage(originalImageDataUrl);
      setCroppedArea(personAvatar.userAvatarCropData);
    }
  }, [data]);

  const onSave = useCallback(
    async (newData) => {
      const payload = {
        personId,
        originalImageData: newData.originalImage,
        croppedImageData: newData.croppedImage,
        cropData: newData.croppedArea,
      };

      await uploadAvatar({ variables: { input: payload } });

      setIsEditing(false);
      setIsNewImage(false);

      onClose();

      await refetchPersonAvatar();
      await onSaveAvatar?.();
    },
    [personId, uploadAvatar, refetchPersonAvatar, onSaveAvatar, onClose]
  );

  const [deleteAvatar] = useMutation(deleteAvatarMutation);

  const onDeleteAvatar = useCallback(async () => {
    setIsDeleting(true);
    await deleteAvatar({ variables: { input: { personId } } });

    await refetchPersonAvatar();
    await onSaveAvatar?.();

    onClose?.();
    setIsEditing(false);
    setIsNewImage(false);
    setAvatarImages();
    setIsDeleting(false);
  }, [
    deleteAvatar,
    personId,
    onClose,
    setAvatarImages,
    refetchPersonAvatar,
    onSaveAvatar,
  ]);

  const onCloseModal = useCallback(() => {
    onClose();
    setIsEditing(false);
    setIsNewImage(false);
    setAvatarImages();
  }, [onClose, setAvatarImages]);

  const onEditCancel = useCallback(() => {
    if (isNewImage) {
      setAvatarImages();
      setIsNewImage(false);
    }
    setIsEditing(false);
  }, [isNewImage, setAvatarImages]);

  const onImageChange = useCallback(async (newImageSrc) => {
    const originalImageBlob = await resizeAndCompressImage(newImageSrc);
    const originalImageBase64 = await blobToBase64(originalImageBlob);
    setOriginalImage(originalImageBase64);
    setIsEditing(true);
    setIsNewImage(true);
  }, []);

  useEffect(() => {
    if (data && !isLoadingPersonAvatar) {
      setAvatarImages();
    }
  }, [data, isLoadingPersonAvatar, setAvatarImages]);

  useEffect(() => {
    if (isOpen) {
      setAvatarImages();
    }
  }, [isOpen, setAvatarImages]);

  if (!isOpen) {
    return null;
  }

  return (
    <Modal
      visible={isOpen}
      onClose={onCloseModal}
      width="fit-content"
      height="fit-content"
      headerContent={<H4>Profile Image</H4>}
    >
      <Flex flexDirection="column" mt="10px">
        {isEditing ? (
          <EditMode
            onImageChange={onImageChange}
            imageSrc={originalImage}
            shape={shape}
            acceptedImageTypes={acceptedImageTypes}
            onCancel={onEditCancel}
            isNewImage={isNewImage}
            onSave={onSave}
            initialCroppedArea={croppedArea}
            isSaving={isLoadingPersonAvatar || isUploading}
          />
        ) : (
          <PreviewMode
            onImageChange={onImageChange}
            imageSrc={croppedImage}
            croppedArea={croppedArea}
            shape={shape}
            acceptedImageTypes={acceptedImageTypes}
            onEdit={() => setIsEditing(true)}
            onDeleteAvatar={onDeleteAvatar}
            isDeleting={isDeleting}
          />
        )}
      </Flex>
    </Modal>
  );
};

AvatarModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  onSaveAvatar: PropTypes.func,
  acceptedImageTypes: PropTypes.arrayOf(PropTypes.string),
  shape: PropTypes.string,
  personId: PropTypes.string.isRequired,
};

export default AvatarModal;
